import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { RefreshCw, Sparkles, X } from 'lucide-react';
import type { ReplyDraftGrounding, ReplyDraftsResult } from '../../types/inbox';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/Spinner';
import { cn } from '../../lib/utils';
import { suggestReply } from './api';

// AI reply drafting — the composer-side control, shared by the comment, review
// and DM detail views.
//
// ⚠️⚠️ ON-DEMAND ONLY. There is deliberately NO useEffect here: drafts are
// generated when the AM clicks, never when a thread is opened or polled.
// Auto-generating would bill for every thread anyone merely looks at, most of
// which are read and closed — and it is pinned by a source-shape test
// (suggestOnDemandOnly.test.ts).
//
// ⚠️ NOTHING IS AUTO-SENT. Picking a draft populates the composer and stops
// there; the AM edits and sends through the existing audited reply path. The
// drafts are labelled as AI-generated on screen so nobody mistakes a suggestion
// for something a colleague wrote.

function groundingLabel(grounding: ReplyDraftGrounding, exampleCount: number): string {
  switch (grounding) {
    case 'account':
      return `Tone matched to this account's last ${exampleCount} ${
        exampleCount === 1 ? 'reply' : 'replies'
      }.`;
    case 'brand':
      return `No replies on this account yet — tone matched to ${exampleCount} recent ${
        exampleCount === 1 ? 'reply' : 'replies'
      } from the same brand.`;
    default:
      return 'No past replies to match — check the tone before sending.';
  }
}

export function SuggestReplyPanel({
  threadId,
  messageId,
  disabled,
  onUse,
  className,
}: {
  threadId: string;
  /** The composer's current reply target; omitted = the latest inbound item. */
  messageId?: string;
  /** Hidden entirely when a reply isn't possible for this target. */
  disabled?: boolean;
  /** Populates the composer. Never sends. */
  onUse: (draft: string) => void;
  className?: string;
}) {
  const [result, setResult] = useState<ReplyDraftsResult | null>(null);
  const [used, setUsed] = useState<number | null>(null);

  const generate = useMutation({
    mutationFn: () => suggestReply(threadId, messageId),
    onSuccess: (data) => {
      setResult(data);
      setUsed(null);
    },
  });

  if (disabled) return null;

  // The server's message is already written for an AM ("busy right now, try
  // again in a moment") — surfacing it inline next to the retry beats a toast
  // that disappears before they can act on it.
  const errorMessage = generate.isError ? (generate.error as Error).message : null;

  if (!result) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => generate.mutate()}
          disabled={generate.isPending}
          title="Generate AI draft replies for this item"
        >
          {generate.isPending ? (
            <Spinner className="h-3.5 w-3.5" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          {generate.isPending ? 'Drafting…' : 'Suggest reply'}
        </Button>
        {errorMessage && <span className="text-xs text-amber-700">{errorMessage}</span>}
      </div>
    );
  }

  return (
    <div className={cn('rounded-lg border border-slate-200 bg-slate-50/70 p-2.5', className)}>
      <div className="mb-1.5 flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5 shrink-0 text-kyck-600" />
        {/* Labelled plainly — these must never read as a colleague's words. */}
        <span className="text-xs font-semibold text-slate-700">AI-generated drafts</span>
        <span className="hidden text-xs text-slate-500 sm:inline">
          — pick one to edit, then send
        </span>
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => generate.mutate()}
          disabled={generate.isPending}
          title="Generate a different set"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-kyck-700 disabled:opacity-50"
        >
          {generate.isPending ? (
            <Spinner className="h-3 w-3" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
          Regenerate
        </button>
        <button
          type="button"
          onClick={() => {
            setResult(null);
            setUsed(null);
            generate.reset();
          }}
          title="Dismiss suggestions"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
        >
          <X className="h-3 w-3" />
          Dismiss
        </button>
      </div>

      <div className="space-y-1.5">
        {result.drafts.map((draft, index) => (
          <button
            key={`${index}-${draft.slice(0, 24)}`}
            type="button"
            onClick={() => {
              onUse(draft);
              setUsed(index);
            }}
            className={cn(
              'block w-full rounded-md border bg-white px-2.5 py-2 text-left text-sm text-slate-800 transition-colors hover:border-kyck-300 hover:bg-kyck-50/40',
              used === index ? 'border-kyck-400 bg-kyck-50/60' : 'border-slate-200',
            )}
          >
            <span className="whitespace-pre-wrap">{draft}</span>
            {used === index && (
              <span className="mt-1 block text-[11px] font-medium text-kyck-700">
                Added to the composer — edit before sending
              </span>
            )}
          </button>
        ))}
      </div>

      <p className="mt-1.5 text-[11px] text-slate-500">
        {groundingLabel(result.grounding, result.exampleCount)} Check any facts — the AI is told
        not to invent details, but it can still be wrong.
      </p>
      {errorMessage && <p className="mt-1 text-xs text-amber-700">{errorMessage}</p>}
    </div>
  );
}
